import type { Filter } from 'Types/filter';

import type { RangeUnit, SelectionSpec, SetValue, UnsetValue } from '../../FilterBarV2.helpers';
import {
	applyRange,
	applySelection,
	formatRangeValue,
	getFilterNumericValue,
	getSelectionValue,
} from '../../FilterBarV2.helpers';

import type { ChipFilterConfig, FilterSectionConfig, RangeFilterConfig } from './AllFiltersContent.consts';
import { FILTER_SECTIONS } from './AllFiltersContent.consts';

let pendingScrollController: Nullable<AbortController> = null;

const RANGE_UNIT_MAP: Record<string, RangeUnit> = {
	ageFrom: 'years',
	displayPriceFrom: 'price',
	maxDistance: 'distance',
	mileageFrom: 'miles',
	previousKeepersCountFrom: 'owners',
};

export const getRangeLabel = (config: RangeFilterConfig, filters: Filter): string => {
	const { fromId, max, min, toId } = config;
	const fromValue = fromId ? getFilterNumericValue({ filters, key: fromId }) : null;
	const toValue = toId ? getFilterNumericValue({ filters, key: toId }) : null;
	const unit = RANGE_UNIT_MAP[fromId ?? toId ?? ''] ?? 'miles';

	return formatRangeValue({ from: fromValue, max, min, to: toValue, unit });
};

const chipToSelectionSpec = (config: ChipFilterConfig): SelectionSpec => ({
	activeValue: config.type === 'boolean' ? config.activeValue : undefined,
	filterId: config.filterId,
	invertSelection: config.type === 'boolean' ? config.invertSelection : undefined,
	kind: config.type,
	options: config.options,
});

export const getChipSelected = (config: ChipFilterConfig, filters: Filter): string[] =>
	getSelectionValue(chipToSelectionSpec(config), filters);

export const applyChipFilter = (config: ChipFilterConfig, selected: string[], setValue: SetValue): void =>
	applySelection(chipToSelectionSpec(config), selected, setValue);

export const applyRangeSection = (
	config: RangeFilterConfig,
	from: Nullable<number>,
	to: Nullable<number>,
	setValue: SetValue,
	unsetValue: UnsetValue,
): void => applyRange({ fromId: config.fromId, toId: config.toId }, from, to, setValue, unsetValue);

export const isSectionDirty = (section: FilterSectionConfig, filters: Filter): boolean => {
	const { config } = section;

	if (config.type === 'range') {
		const fromValue = config.fromId ? getFilterNumericValue({ filters, key: config.fromId }) : null;
		const toValue = config.toId ? getFilterNumericValue({ filters, key: config.toId }) : null;
		return fromValue != null || toValue != null;
	}

	if (config.type === 'radio') {
		const fromValue = getFilterNumericValue({ filters, key: config.fromId });
		const toValue = getFilterNumericValue({ filters, key: config.toId });
		return fromValue != null || toValue != null;
	}

	if (config.type === 'toggle') {
		// The seen filter is a session preference managed separately via useSeenFilter,
		// not part of the filter store. Once it's migrated into the store, this branch
		// can be removed and isSectionDirty will handle it automatically.
		// See also: getModalOnlyFilterCount TODO.
		return false;
	}

	return getChipSelected(config, filters).length > 0;
};

export const getInitialOpenSections = (filters: Filter): Set<string> => {
	const openSections = new Set<string>();
	for (const section of FILTER_SECTIONS) {
		if (isSectionDirty(section, filters)) {
			openSections.add(section.id);
		}
	}
	return openSections;
};

export const getApplicableSections = ({
	isSeenFilterEnabled,
	sections,
}: {
	isSeenFilterEnabled: boolean;
	sections: FilterSectionConfig[];
}): FilterSectionConfig[] => sections.filter((section) => section.config.type !== 'toggle' || isSeenFilterEnabled);

export const scrollSectionIntoView = (sectionId: string): void => {
	const element = document.getElementById(`all-filters-accordion-${sectionId}`);
	if (!element) return;

	pendingScrollController?.abort();
	pendingScrollController = new AbortController();

	element.addEventListener('transitionend', () => element.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), {
		once: true,
		signal: pendingScrollController.signal,
	});
};
